// WhatsApp / Instagram chat view: same layout as the reference product's channel chat modal (title, brand
// divider, status row, Stop/Start AI replies, date-grouped bubbles). Demo additions: WhatsApp-style
// ticks on sent messages, "typing…" while the AI answers, a reply box for staff, and Clear AI memory.
//
// A human-intervention request is an alert only. The AI keeps answering until staff press Stop or
// reply here (a takeover); Start or Mark resolved hands the chat back to the AI.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FaRobot, FaUser, FaUserTie, FaBell, FaInstagram, FaWhatsapp, FaFileAlt, FaMapMarkerAlt } from 'react-icons/fa';
import api, { formatPhone, SALON_NAME } from '../services/api';
import { BranchTag } from '../context/EngagementScope';
import CustomerProfileModal from './CustomerProfileModal';
import {
  Confirm,
  InterestTag,
  InterventionBadge,
  LeadFlag,
  Loader,
  Spinner,
  UpcomingTag,
  INSTAGRAM_COLOR,
  instagramHandle,
} from './ui';

const POLL_MS = 4000;
const TYPING_WINDOW_MS = 90 * 1000;

const SENDER_LABEL = { ai: `${SALON_NAME} AI`, staff: 'Staff', auto: 'Automated message' };

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function dayLabel(raw) {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw || '');
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function timeLabel(raw) {
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/** Date dividers, plus a marker where AI memory was cleared. */
function buildItems(messages, memoryResetAt) {
  const out = [];
  let lastDay = '';
  let markerPlaced = !memoryResetAt;
  messages.forEach((m) => {
    if (!markerPlaced && m.created_at >= memoryResetAt) {
      out.push({ type: 'memory', key: 'memory' });
      markerPlaced = true;
    }
    const day = String(m.created_at || '').slice(0, 10);
    if (day !== lastDay) {
      out.push({ type: 'date', key: `d-${day}`, label: dayLabel(m.created_at) });
      lastDay = day;
    }
    out.push({ type: 'msg', key: `m-${m.id}`, msg: m });
  });
  if (!markerPlaced) out.push({ type: 'memory', key: 'memory' });
  return out;
}

/** WhatsApp-style delivery ticks for messages Naturals sent. */
export function Ticks({ status }) {
  if (status === 'failed') return <span className="de-ticks de-ticks--failed" title="Not delivered">⚠</span>;
  if (status === 'read') return <span className="de-ticks de-ticks--read" title="Read">✓✓</span>;
  if (status === 'delivered') return <span className="de-ticks" title="Delivered">✓✓</span>;
  if (status === 'sent') return <span className="de-ticks" title="Sent">✓</span>;
  return null;
}

function OutAvatar({ sender }) {
  if (sender === 'staff') {
    return (
      <div className="de-chat-avatar de-chat-avatar--staff" title="Staff">
        <FaUserTie size={14} />
      </div>
    );
  }
  if (sender === 'auto') {
    return (
      <div className="de-chat-avatar de-chat-avatar--auto" title="Automated message">
        <FaBell size={13} />
      </div>
    );
  }
  return (
    <div className="de-chat-avatar de-chat-avatar--bot" title={`${SALON_NAME} AI`}>
      <FaRobot size={15} />
    </div>
  );
}

const MEDIA_ICON = { image: '📷', voice: '🎤', audio: '🎵', video: '🎬', sticker: '🌟', document: '📄', location: '📍', contacts: '👤', reaction: '💬', unsupported: '⚠️' };

/** The "[Voice message]" tag the backend puts in front of a media message's caption. */
function splitMediaBody(body) {
  const match = /^\[([^\]]+)\]\s*([\s\S]*)$/.exec(String(body || ''));
  return match ? { tag: match[1], caption: match[2] } : { tag: '', caption: String(body || '') };
}

/** Photo, voice note, video, sticker or document a customer sent, loaded from object storage. */
function MediaContent({ msg }) {
  const { tag, caption } = splitMediaBody(msg.body);
  const type = msg.media_type;
  const url = msg.media_url;
  const mime = String(msg.media_mime || '').split(';')[0];
  let media = null;
  if (url && (type === 'image' || type === 'sticker')) {
    media = (
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={tag} className={type === 'sticker' ? 'de-msg-sticker' : 'de-msg-image'} loading="lazy" />
      </a>
    );
  } else if (url && (type === 'voice' || type === 'audio')) {
    media = <audio className="de-msg-audio" controls preload="metadata" src={url} />;
  } else if (url && type === 'video') {
    media = <video className="de-msg-video" controls preload="metadata" src={url} />;
  } else if (url) {
    media = (
      <a href={url} target="_blank" rel="noreferrer" className="de-msg-file">
        <FaFileAlt size={15} /> {caption || tag} {mime ? <span className="de-muted">({mime})</span> : null}
      </a>
    );
  }
  const showCaption = caption && !(url && type === 'document');
  return (
    <>
      <div className="de-msg-media-tag">
        {type === 'location' ? <FaMapMarkerAlt size={11} /> : MEDIA_ICON[type] || '📎'} {tag}
        {!url && ['image', 'voice', 'audio', 'video', 'sticker', 'document'].includes(type) ? (
          <span className="de-muted"> · file not saved</span>
        ) : null}
      </div>
      {media}
      {showCaption ? <div className="de-msg-caption">{caption}</div> : null}
    </>
  );
}

export default function ChatModal({ customerId, channel = 'whatsapp', conversationId = null, flag, onClose, onChanged }) {
  const isInstagram = channel === 'instagram';
  const channelLabel = isInstagram ? 'Instagram' : 'WhatsApp';
  const [profileOpen, setProfileOpen] = useState(false);
  // The chat this window is about: the one asked for, or (older links) the customer's main-number chat.
  const [chatId, setChatId] = useState(conversationId);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [, setTick] = useState(0);
  const bodyRef = useRef(null);
  const lastCount = useRef(0);

  const load = useCallback(
    (silent) =>
      api
        .listMessages(customerId, channel, chatId)
        .then((data) => {
          setThread(data);
          if (data?.conversation_id && !chatId) setChatId(data.conversation_id);
        })
        .catch((e) => !silent && toast.error(e.message))
        .finally(() => setLoading(false)),
    [customerId, channel, chatId],
  );

  useEffect(() => {
    setLoading(true);
    lastCount.current = 0;
    load(false);
    const t = setInterval(() => {
      load(true);
      setTick((n) => n + 1); // re-evaluate the typing indicator window
    }, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const messages = useMemo(() => thread?.messages || [], [thread?.messages]);
  const customer = thread?.customer;
  const handle = instagramHandle(customer?.instagram_username);
  const name = isInstagram ? handle || customer?.name || 'Customer' : customer?.name || formatPhone(customer?.phone) || 'Customer';
  const agentActive = thread ? !thread.agent_paused : true;
  const last = messages[messages.length - 1];
  const aiTyping = Boolean(
    thread && agentActive && last && last.direction === 'in' && Date.now() - new Date(last.created_at).getTime() < TYPING_WINDOW_MS,
  );
  const items = useMemo(() => buildItems(messages, thread?.memory_reset_at), [messages, thread?.memory_reset_at]);

  useEffect(() => {
    const count = messages.length + (aiTyping ? 1 : 0);
    if (count !== lastCount.current && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
    lastCount.current = count;
  }, [messages.length, aiTyping]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !confirmReset && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, confirmReset]);

  const after = async (message) => {
    if (message) toast.success(message);
    await load(true);
    if (onChanged) onChanged();
  };

  const toggleAgent = async () => {
    setToggling(true);
    try {
      if (agentActive) {
        await api.pauseAgent(customerId, chatId, channel);
        await after('You have taken over this chat. The AI stays quiet until you press Start.');
      } else {
        await api.resumeAgent(customerId, chatId, channel);
        await after('The AI is answering this chat again.');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setToggling(false);
    }
  };

  const resolve = async () => {
    if (!thread?.handoff) return;
    try {
      await api.resolveHandoff(thread.handoff.id);
      await after('Marked as resolved. The AI continues this chat.');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const resetMemory = async () => {
    setConfirmReset(false);
    try {
      await api.resetMemory(customerId, chatId, channel);
      await after('AI memory cleared. The next message starts a fresh conversation.');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const messagesPaused = (() => {
    const until = thread?.customer?.automation_paused_until;
    return !!until && new Date(until).getTime() > Date.now();
  })();

  const toggleAutomations = async () => {
    setPausing(true);
    try {
      if (messagesPaused) await api.resumeCustomerAutomations(customerId);
      else await api.pauseCustomerAutomations(customerId);
      await load(true);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPausing(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    try {
      const res = await api.sendReply(customerId, text, channel, chatId);
      setDraft('');
      if (res && res.sent === false) toast.error(`Saved, but ${channelLabel} did not send: ${res.error}`);
      else toast.success('Reply sent. You have taken over; press Start to hand back to the AI.');
      await load(true);
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="de-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`de-chat de-fade${isInstagram ? ' de-chat--instagram' : ''}`} onMouseDown={(e) => e.stopPropagation()}>
        <div className="de-row" style={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className="de-chat-title">
              {channelLabel} : {name}
            </div>
            {thread ? (
              <div className="de-row de-row--wrap" style={{ gap: 6, marginTop: 5 }}>
                {thread.channel_label && thread.channel_label !== 'Main number' ? (
                  <span className="de-pill de-pill--info de-pill--sm" title="The number this chat is on">{thread.channel_label}</span>
                ) : null}
                <BranchTag franchiseId={thread.franchise_id} name={thread.branch_name} queue={thread.franchise_id == null} />
                {(thread.conversations || []).length > 1 ? (
                  <select className="de-select" style={{ padding: '2px 8px', fontSize: 12 }} value={chatId || ''} onChange={(e) => setChatId(Number(e.target.value))} title="This customer's chats on other numbers">
                    {thread.conversations.map((c) => (
                      <option key={c.conversation_id} value={c.conversation_id}>
                        {c.channel_label}
                      </option>
                    ))}
                  </select>
                ) : null}
                <button type="button" className="de-btn de-btn--ghost de-btn--xs" onClick={() => setProfileOpen(true)}>
                  👤 Profile
                </button>
                <button type="button" className="de-btn de-btn--ghost de-btn--xs" disabled={pausing} onClick={toggleAutomations}>
                  {messagesPaused ? 'Resume messages' : 'Pause messages'}
                </button>
              </div>
            ) : null}
            {customer && (customer.phone || isInstagram) ? (
              <div className="de-small de-muted" style={{ marginTop: 5 }}>
                {isInstagram ? (
                  <>
                    {handle ? `Instagram ID: ${handle.slice(1)}` : 'Instagram DM'}
                    {customer.phone ? ` · Mobile: ${formatPhone(customer.phone)}` : ' · Mobile not shared yet'}
                  </>
                ) : (
                  <>Phone: {formatPhone(customer.phone)}</>
                )}
                {aiTyping ? <span className="de-typing-label"> · typing…</span> : null}
              </div>
            ) : null}
          </div>
          <div className="de-row" style={{ gap: 10, flexShrink: 0 }}>
            {thread ? (
              <button
                type="button"
                className={`de-btn de-btn--xs ${agentActive ? 'de-btn--danger-solid' : 'de-btn--success-solid'}`}
                onClick={toggleAgent}
                disabled={toggling}
                title={agentActive ? 'Take over: stop AI replies for this chat' : 'Hand back: let the AI answer again'}
              >
                {toggling ? <Spinner light /> : null}
                {agentActive ? 'Stop' : 'Start'}
              </button>
            ) : null}
            {isInstagram ? <FaInstagram size={22} color={INSTAGRAM_COLOR} /> : <FaWhatsapp size={22} color="#25D366" />}
            <button type="button" className="de-modal-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        <div className="de-chat-divider" />

        {thread ? (
          <div className="de-row de-row--wrap" style={{ marginBottom: 12 }}>
            {flag ? <LeadFlag status={flag} /> : null}
            <span className={`de-pill de-pill--dot ${agentActive ? 'de-pill--ok' : 'de-pill--warn'}`}>
              {agentActive ? 'AI replies: On' : customer?.bot_detected ? 'AI paused: bot detected' : 'AI paused: staff have taken over'}
            </span>
            {customer?.bot_detected ? (
              <>
                <span className="de-pill de-pill--danger">🤖 Bot detected</span>
                <span className="de-small de-muted" title="Press Start if this is a real person">{customer.bot_reason}</span>
              </>
            ) : null}
            {thread.handoff ? (
              <>
                <InterventionBadge />
                <span className="de-small de-muted">{thread.handoff.reason}</span>
                <button type="button" className="de-btn de-btn--outline de-btn--xs" onClick={resolve}>
                  Mark resolved
                </button>
              </>
            ) : null}
            <span className="de-spacer" />
            <button
              type="button"
              className="de-btn de-btn--ghost de-btn--xs"
              onClick={() => setConfirmReset(true)}
              title="The AI forgets earlier messages; they stay visible here"
            >
              🧹 Clear AI memory
            </button>
          </div>
        ) : null}

        {thread?.upcoming?.length || thread?.interest_service ? (
          <div className="de-chat-upcoming">
            {(thread.upcoming || []).map((b) => (
              <UpcomingTag key={b.id} bookings={[b]} />
            ))}
            <InterestTag service={thread.interest_service} />
          </div>
        ) : null}

        {loading && !thread ? (
          <Loader text="Loading messages…" />
        ) : (
          <div className="de-chat-body" ref={bodyRef}>
            {items.length === 0 ? (
              <div className="de-empty">No messages yet.</div>
            ) : (
              items.map((item) => {
                if (item.type === 'date') {
                  return (
                    <div key={item.key} className="de-chat-date">
                      {item.label}
                    </div>
                  );
                }
                if (item.type === 'memory') {
                  return (
                    <div key={item.key} className="de-chat-memory">
                      AI memory cleared here. Earlier messages are not used for replies.
                    </div>
                  );
                }
                const m = item.msg;
                if (m.direction === 'in') {
                  return (
                    <div key={item.key} className="de-msg de-msg--in">
                      <div style={{ maxWidth: '82%' }}>
                        <div className="de-msg-inner" style={{ maxWidth: '100%' }}>
                          <div className={`de-msg-bubble${m.media_type && m.media_type !== 'comment' ? ' de-msg-bubble--media' : ''}`}>
                            {m.media_type === 'comment' ? (
                              <>
                                <div className="de-msg-media-tag">Comment on a post</div>
                                <div>{m.body}</div>
                              </>
                            ) : m.media_type ? <MediaContent msg={m} /> : m.body}
                          </div>
                          <div className="de-chat-avatar de-chat-avatar--customer" title={name}>
                            {initials(customer?.name) || <FaUser size={13} />}
                          </div>
                        </div>
                        <div className="de-msg-time">{timeLabel(m.created_at)}</div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={item.key} className={`de-msg de-msg--out${m.sender === 'staff' ? ' de-msg--staff' : ''}${m.sender === 'auto' ? ' de-msg--auto' : ''}`}>
                    <div className="de-msg-inner">
                      <OutAvatar sender={m.sender} />
                      <div className="de-msg-bubble">
                        {m.media_type === 'comment' ? <div className="de-msg-media-tag">Replied on the comment</div> : null}
                        {m.body}
                        <div className="de-msg-meta">
                          {SENDER_LABEL[m.sender] || SALON_NAME} · {timeLabel(m.created_at)} <Ticks status={m.status} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            {aiTyping ? (
              <div className="de-msg de-msg--out">
                <div className="de-msg-inner">
                  <OutAvatar sender="ai" />
                  <div className="de-msg-bubble de-typing" aria-label={`${SALON_NAME} AI is typing`}>
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}

        <form className="de-chat-foot" onSubmit={send}>
          <input
            className="de-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={thread && thread.can_reply === false}
            placeholder={
              thread && thread.can_reply === false
                ? 'This chat is now with another branch; only that branch and HQ can reply.'
                : agentActive
                  ? `Reply as ${SALON_NAME} staff (this takes over from the AI)…`
                  : `Reply as ${SALON_NAME} staff…`
            }
          />
          <button type="submit" className="de-btn de-btn--primary" disabled={sending || !draft.trim() || (thread && thread.can_reply === false)}>
            {sending ? <Spinner light /> : null}
            Send
          </button>
        </form>
      </div>

      {profileOpen ? <CustomerProfileModal customerId={customerId} onClose={() => setProfileOpen(false)} /> : null}
      {confirmReset ? (
        <Confirm
          title="Clear AI memory?"
          message="The AI forgets this chat's earlier messages and any booking it was about to confirm. The messages stay visible here. Use this to start a fresh test conversation."
          confirmLabel="Clear memory"
          danger={false}
          onConfirm={resetMemory}
          onCancel={() => setConfirmReset(false)}
        />
      ) : null}
    </div>
  );
}
